import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import { shortId } from './Ids';

/**
 * A reference to another record, shown by its human name (customer name, subject
 * display name, registry name, domain, staff e-mail…) and linking to its page.
 * The UUID is never the visible text, the link's title or its accessible name when a name
 * exists; without one, the short ID is shown with the full one as its tooltip. Use this
 * instead of IdLink/PlainId for anything a person would recognise.
 *
 *   <EntityRef to={`/customers/${c.customer_id}`} label={c.customer_name} id={c.customer_id} />
 *   <EntityRef label={row.registry_name} sub={row.registry_code} to={`/registry/registries/${row.registry_code}`} />
 */
export function EntityRef({
  label,
  to,
  id,
  sub,
  className,
}: {
  /** Readable name. When missing, a short ID is shown in muted monospace as a last resort. */
  label?: ReactNode;
  to?: string;
  /** Full identifier: the tooltip of the short ID shown when there is no label. */
  id?: string | null;
  /** Secondary text under or after the name (code, type, masked detail). */
  sub?: ReactNode;
  className?: string;
}) {
  if (!label && !id) return <span className="text-muted-foreground">—</span>;
  const text = label ? (
    <span className="font-medium">{label}</span>
  ) : (
    <code className="text-xs text-muted-foreground" title={id ?? undefined}>
      {shortId(id ?? '')}
    </code>
  );
  const body = (
    <>
      {text}
      {sub ? <span className="block text-xs font-normal text-muted-foreground">{sub}</span> : null}
    </>
  );
  return to ? (
    <Link to={to} className={cn('inline-block max-w-full text-primary hover:underline', className)}>
      {body}
    </Link>
  ) : (
    <span className={cn('inline-block max-w-full', className)}>
      {body}
    </span>
  );
}
